import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.details.addEventListener('toggle', this.#syncPageInert);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#syncPageInert);
    this.#drawerShownObserver.disconnect();
    this.#setPageInert(false);
  }

  /**
   * The page outside the header, made inert while the drawer is open.
   * Tab already stays inside the drawer (trapFocus), but a screen reader's
   * own cursor (swiping, not tabbing) could still reach the page under the
   * backdrop (known issue T26). The header itself is left alone: the drawer
   * lives inside it.
   * @type {HTMLElement[]}
   */
  #inertElements = [];

  /**
   * Makes the page inert exactly while the drawer is open and on screen, and
   * lets it go on every way of closing: the menu button, the close button,
   * Escape, the backdrop, following a link. `toggle` fires whenever the
   * drawer's <details> opens or closes, however that happens. While it is
   * open, whether it is shown is watched too: when the window widens the
   * header can switch to the menu row and hide the drawer (it does so a
   * moment after the resize), and an open but hidden drawer must not leave
   * the page unusable. A ResizeObserver reports the <details> collapsing to
   * nothing, and coming back.
   */
  #syncPageInert = () => {
    const open = this.isOpen;
    const shown = open && this.refs.details.getClientRects().length > 0;

    if (open) this.#drawerShownObserver.observe(this.refs.details);
    else this.#drawerShownObserver.disconnect();

    this.#setPageInert(shown);
  };

  #drawerShownObserver = new ResizeObserver(() => this.#syncPageInert());

  /**
   * @param {boolean} inert
   */
  #setPageInert(inert) {
    if (!inert) {
      for (const element of this.#inertElements) element.inert = false;
      this.#inertElements = [];
      return;
    }

    if (this.#inertElements.length) return;

    // Everything beside the header's own section and the header group, up to
    // the page wrapper: the main content and the footer (and any other
    // section placed in the header group).
    const wrapper = this.closest('.page-wrapper');
    for (let node = /** @type {Element} */ (this); wrapper && node.parentElement && node !== wrapper; node = node.parentElement) {
      const parent = node.parentElement;
      if (parent !== wrapper && parent.id !== 'header-group') continue;

      for (const sibling of parent.children) {
        if (sibling === node || !(sibling instanceof HTMLElement) || sibling.inert) continue;
        if (sibling instanceof HTMLScriptElement || sibling instanceof HTMLTemplateElement) continue;
        sibling.inert = true;
        this.#inertElements.push(sibling);
      }
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    // Close the innermost menu that is actually open. Focus usually rests on a
    // room's summary, whose own <details> is closed; Escape used to "close"
    // that one and leave the drawer open, however often it was pressed.
    const details =
      (event.target instanceof Element && event.target.closest('details[open]')) || this.refs.details;
    this.#close(/** @type {HTMLDetailsElement} */ (details));

    // Whatever had focus is being hidden: hand it to that menu's own button
    // (the menu button in the header when the whole drawer closes).
    details.querySelector('summary')?.focus();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // The page comes back as the drawer starts to close, not when its
    // animation ends (the <details> only loses `open` then). Stop watching
    // the drawer's size too, or a resize during the animation would find it
    // still open and make the page inert again.
    if (details === this.refs.details) {
      this.#drawerShownObserver.disconnect();
      this.#setPageInert(false);
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);

          // Back to the menu button, as Escape already does (known issue T28:
          // the close button and the backdrop left focus on the page itself,
          // so a keyboard or screen-reader user started again from the top).
          // Only when focus was lost or is still in the drawer; never taken
          // from somewhere else.
          const active = document.activeElement;
          if (!active || active === document.body || details.contains(active)) {
            summary.focus({ preventScroll: true });
          }
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
